import styles from '../../ablineForm.module.scss';
import RadioGroup from 'components/global/RadioGroup';
import getOption from 'utils/helpers/getOption';
import FileUploadModal from '../../../FileUploadModal';
import FormElement from 'components/global/FormElement';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { parseShapefileSet } from 'utils/ShapefileUtils';
import { useFormikContext } from 'formik';

interface ABLineCreationOptionsProps {
  generatePreviewIfValid: (name: string, value?: string) => void;
  trial: Record<string, any>;
  generatePreview?: (values?: Record<string, any>) => void;
}

const ABLineCreationOptions = ({
  generatePreviewIfValid,
  trial,
  generatePreview,
}: ABLineCreationOptionsProps) => {
  const [radioGroupOption, setRadioGroupOption] = useState(
    trial.get_abline_method || 'guess',
  );
  const [abLineError, setABLineError] = useState<string | null>(null);
  const [isABLineLoading, setIsABLineLoading] = useState(false);
  const { setFieldValue, values } = useFormikContext();

  // Check if we're using the demo field boundary
  const isUsingDemoField =
    trial.field_boundary_files_meta &&
    trial.field_boundary_files_meta.length === 1 &&
    trial.field_boundary_files_meta[0] === 'demo_field__boundary.geojson';

  useEffect(() => {
    // set the default AB line method when it's changed
    setRadioGroupOption(trial.get_abline_method);
  }, [trial.get_abline_method]);

  // Restore AB line file state when navigating back
  useEffect(() => {
    if (
      trial.abline_files_meta &&
      trial.abline_files_meta.length > 0 &&
      (!values.abline_files ||
        (Array.isArray(values.abline_files) &&
          values.abline_files.length === 0))
    ) {
      setFieldValue('abline_files', 'trial');
    }
  }, [trial, setFieldValue, values.abline_files]);

  useEffect(() => {
    // If using demo field and the current option is 'upload', switch to 'demo'
    if (isUsingDemoField && radioGroupOption === 'upload') {
      generatePreviewIfValid('get_abline_method', 'demo');
    }

    // Auto-generate demo AB line when using demo field and demo method
    if (
      isUsingDemoField &&
      radioGroupOption === 'demo' &&
      !trial.abline_geojson
    ) {
      // Automatically trigger demo AB line creation
      generatePreview({ get_abline_method: 'demo' });
    }
  }, [
    isUsingDemoField,
    radioGroupOption,
    generatePreviewIfValid,
    generatePreview,
    trial.abline_geojson,
  ]);

  const debouncedCallback = useDebouncedCallback(
    (value: string | undefined) => {
      const numericValue = Number(value);
      if (value !== '' && numericValue >= 0 && numericValue <= 360) {
        generatePreviewIfValid('abline_heading', value);
      }
    },
    750,
  );

  // Process uploaded AB line files and convert to GeoJSON
  const handleABLineUpload = async (files: File[]) => {
    // Clear any previous errors
    setABLineError(null);

    // Convert files to GeoJSON immediately
    if (files.length > 0) {
      try {
        setIsABLineLoading(true);
        const geojson = await parseShapefileSet(files);

        // Create a complete values object with all the data directly
        // Don't rely on form values that are set asynchronously
        const completeValues = {
          get_abline_method: 'upload',
          abline_geojson: geojson,
          abline_files_meta: files.map((file) => file.name),
        };

        // Update form values for future reference
        setFieldValue('get_abline_method', 'upload');
        setFieldValue('abline_geojson', geojson);
        setFieldValue(
          'abline_files_meta',
          files.map((file) => file.name),
        );
        setFieldValue('abline_gdf', null);
        setFieldValue('abline_files', 'trial');

        // If we have direct access to generatePreview use it, otherwise fallback
        if (generatePreview) {
          // Call with our complete values to avoid any race conditions
          generatePreview(completeValues);
        } else {
          // Fallback to old method if we must
          generatePreviewIfValid('get_abline_method', 'upload');
        }

        setIsABLineLoading(false);
      } catch (error) {
        setABLineError(
          error instanceof Error
            ? error.message
            : 'Error converting AB line files',
        );
        setIsABLineLoading(false);
      }
    } else {
      setFieldValue('abline_geojson', null);
    }
  };

  return (
    <div>
      <div
        className={styles['abline-form__method-select']}
        data-testid="abline-form__method-select"
      >
        <RadioGroup
          name="get_abline_method"
          radioGroupHeader={'How do you want to make your starting AB line?'}
          radioGroupFooter={
            'Note: Your starting AB line can be adjusted later.'
          }
          options={[
            // If using demo field, show 'Use demo AB line' option
            isUsingDemoField
              ? getOption('demo', 'Use demo AB line')
              : getOption('upload', 'I have an AB line'),
            getOption('heading', 'I have a heading'),
            getOption('guess', "Use FarmTest's initial guess"),
          ]}
          defaultValue={radioGroupOption}
          onChange={(event) => {
            generatePreviewIfValid('get_abline_method', event.target.value);
          }}
        />
      </div>

      {trial.get_abline_method === 'upload' && !isUsingDemoField && (
        <>
          <FileUploadModal
            name="abline_files"
            metaName="abline_files_meta"
            title="AB line"
            fileDescription="Select the AB line shapefile."
            uploadAction={handleABLineUpload}
          />
          {isABLineLoading && (
            <div className={styles['loading-indicator']}>
              Converting AB line shapefile...
            </div>
          )}
          {abLineError && (
            <div className={styles['error-message']}>{abLineError}</div>
          )}
        </>
      )}

      {trial.get_abline_method === 'heading' && (
        <FormElement
          name="abline_heading"
          label="Enter AB line heading in degrees:"
          tooltip="Heading 0 = Due North, Heading increases clockwise"
          rightElement="°"
          onChangeCallback={(event: {
            target: { value: string | undefined };
          }) => {
            debouncedCallback(event.target.value);
          }}
        />
      )}
    </div>
  );
};

export default ABLineCreationOptions;
